// Facility Console Drawer - Right-side drawer for facility and store management
import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { X, ChevronDown } from 'lucide-react';
import { useFacilityConsole } from '../../context/FacilityConsoleContext';
import { useFocusTrap, useScrollLock } from '../../hooks/useFocusTrap';
import { FACILITY_ITEMS, TASK_ITEMS, AI_ITEMS, STORE_ITEMS } from '../../config/FacilityConsoleNav';
import DrawerLink from './DrawerLink';
import { useIsJuniorAt } from '../../utils/juniorLanding';
import '../../styles/facility-console-drawer.css';

const FacilityConsoleDrawer = () => {
  const { facilityId: routeFacilityId } = useParams();
  
  // Log mount/unmount for diagnostics
  useEffect(() => {
    console.debug('[FacilityConsole] mounted');
    
    // Safety check for legacy navigation components
    if (document.querySelector('.facility-options, .store-options')) {
      console.warn('[Cleanup] Legacy in-page side nav still present. Remove FacilityOptions/StoreOptions mounts.');
    }
    
    return () => {
      console.debug('[FacilityConsole] unmounted');
    };
  }, []);
  
  const {
    facilities,
    selectedFacilityId,
    drawerOpen,
    closeDrawer,
    switchFacility,
    hasFacilityAccess,
    isOwnerOrManager,
    selectedFacilityRole,
  } = useFacilityConsole();

  // R-TRAINING junior Cowork fix 4 (ruled 2026-11-04): a JUNIOR's sidebar
  // has no MY STORE and no AI. The flag is the `froggy-u/` `landing` the
  // sign-in landing reads (`useIsJuniorAt`, one reading). Owners,
  // Managers and members with no role here answer `false` at once — their
  // sidebar is exactly as before; a Staff member is UNKNOWN (null) until
  // the flag is read, and the two sections wait for it.
  const junior = useIsJuniorAt(selectedFacilityId, selectedFacilityRole);
  const hideJuniorSections = junior !== false;

  const drawerRef = useFocusTrap(drawerOpen);
  const toggleButtonRef = useRef(null);
  
  // Use scroll lock when drawer is open
  useScrollLock(drawerOpen);
  
  // Sync facility context with route params
  useEffect(() => {
    if (routeFacilityId && routeFacilityId !== selectedFacilityId) {
      console.debug('[FacilityContext] syncing from route', { 
        routeFacilityId, 
        currentSelected: selectedFacilityId 
      });
      switchFacility(routeFacilityId);
    }
  }, [routeFacilityId, selectedFacilityId, switchFacility]);
  
  // If no facility is selected but we have facilities, default to first one
  useEffect(() => {
    if (!selectedFacilityId && facilities.length > 0) {
      const defaultFacilityId = routeFacilityId || facilities[0].id;
      console.debug('[FacilityConsole] defaulting to facility', { 
        defaultFacilityId, 
        fromRoute: !!routeFacilityId 
      });
      switchFacility(defaultFacilityId);
    }
  }, [selectedFacilityId, facilities, routeFacilityId, switchFacility]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && drawerOpen) {
        closeDrawer();
      }
    };

    if (drawerOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [drawerOpen, closeDrawer]);

  // #52 smell pass 2026-08-12: a species-fetch effect lived here feeding
  // the legacy FroggyChatBubble modal below — but NO nav item ever set
  // isModal, so the modal was unreachable while the fetch ran live on
  // every facility switch. Effect, modal, state and the flat legacy
  // component all removed; the real bubble is components/
  // FroggyChatBubble/FroggyChatBubble.jsx, mounted globally by App.js.

  if (!hasFacilityAccess) {
    console.debug('[FacilityConsoleDrawer] no facility access, not rendering drawer');
    return null;
  }
  
  console.debug('[FacilityConsoleDrawer] rendering drawer', { 
    drawerOpen, 
    selectedFacilityId, 
    facilitiesCount: facilities.length 
  });

  const handleFacilityChange = (e) => {
    const newFacilityId = e.target.value;
    console.debug('[FacilityConsole] facility switch', { 
      from: selectedFacilityId, 
      to: newFacilityId 
    });
    switchFacility(newFacilityId);
  };

  // Get current facility name
  const currentFacility = facilities.find(f => f.id === selectedFacilityId);
  const currentFacilityName = currentFacility?.facility_name || 'Select Facility';

  // Build navigation items with proper hrefs
  const facilityItems = FACILITY_ITEMS.map(item => ({
    ...item,
    href: selectedFacilityId ? item.buildHref(selectedFacilityId) : null
  }));

  // Filter task items based on role - hide managerOnly items for non-managers
  const taskItems = TASK_ITEMS
    .filter(item => !item.managerOnly || isOwnerOrManager)
    .map(item => ({
      ...item,
      href: selectedFacilityId ? item.buildHref(selectedFacilityId) : null
    }));

  const aiItems = AI_ITEMS.map(item => ({
    ...item,
    href: selectedFacilityId ? item.buildHref(selectedFacilityId) : null
  }));

  // Filter store items by role — Marketing is manager+ only.
  const storeItems = STORE_ITEMS
    .filter(item => !item.managerOnly || isOwnerOrManager)
    .map(item => ({
      ...item,
      href: selectedFacilityId ? item.buildHref(selectedFacilityId, selectedFacilityId) : null
    }));

  // Define menu sections with proper navigation
  const menuSections = [
    {
      title: 'Facility',
      section: 'facilities',
      items: facilityItems
    },
    {
      title: 'Tasks & Feedings',
      section: 'tasks',
      items: taskItems
    },
    {
      title: 'My Store',
      section: 'stores',
      items: storeItems,
      juniorHidden: true,
    },
    {
      title: 'AI',
      section: 'ai',
      items: aiItems,
      juniorHidden: true,
    }
  ].filter((section) => !(section.juniorHidden && hideJuniorSections));

  return (
    <>
      {/* Overlay */}
      {drawerOpen && (
        <div 
          className="facility-drawer-overlay" 
          onClick={() => {
            console.debug('[FacilityConsole] backdrop click – closing');
            closeDrawer();
          }}
          aria-hidden="true"
        />
      )}
      
      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`facility-drawer ${drawerOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Facility Console"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="facility-drawer-header">
          <div className="facility-drawer-header-content">
            <h3>Facility Console</h3>
            <button 
              className="ff-btn ff-btn--ghost ff-btn--icon ff-btn--sm facility-drawer-close"
              onClick={closeDrawer}
              aria-label="Close Facility Console"
              ref={toggleButtonRef}
            >
              <X size={16} />
            </button>
          </div>
          
          {/* Facility Selector */}
          {facilities.length > 1 && (
            <div className="facility-selector">
              <label htmlFor="facility-select" className="sr-only">
                Select Facility
              </label>
              <select 
                id="facility-select"
                value={selectedFacilityId || ''}
                onChange={handleFacilityChange}
                className="facility-select"
              >
                {facilities.map(facility => (
                  <option key={facility.id} value={facility.id}>
                    {facility.facility_name || 'Unnamed Facility'}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="facility-select-icon" />
            </div>
          )}
          {facilities.length === 1 && (
            <div className="facility-single">
              <span className="facility-single-label">Current Facility:</span>
              <span className="facility-single-name">{currentFacilityName}</span>
            </div>
          )}
        </div>

        <div className="facility-drawer-content">
          {menuSections.map(section => (
            <div key={section.title} className="facility-menu-section">
              <h4 className="facility-menu-section-title">{section.title}</h4>
              <ul className="facility-menu">
                {section.items.map(item => (
                  <li key={item.key}>
                    <DrawerLink
                      label={item.label}
                      href={item.href}
                      onNavigate={closeDrawer}
                      section={section.section}
                      itemKey={item.key}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default FacilityConsoleDrawer;